import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import InstallShortcutBanner from "../components/InstallShortcutBanner";
import { useLocalization } from "../hooks/useLocalization";
import {
  BackgroundFixed,
  BgCover,
  BgVignette,
  BrandLeft,
  ContentInner,
  ContentWrap,
  DustOverlay,
  FooterOverlay,
  FooterSpacer,
  FooterWrap,
  HeaderPill,
  HeaderWrap,
  PoweredText,
  Shell,
  WhoscallLogo,
} from "./components";

/* Layout Options (per-page) */
type LayoutOptions = {
  /** 背景圖向下位移（px）。正數＝往下看、負數＝往上看 */
  bgOffsetY?: number;
  /** Footer 是否固定在底部 */
  footerFixed?: boolean;
  /** Footer 基礎高度（行動） */
  footerHeight?: number; // mobile base (預設 38)
  /** 內容區域的 overflow-Y */
  overflowY?: "scroll" | "hidden";
  /** （可選）桌機內容最大寬度；預設 1240 */
  desktopMaxWidth?: number;
  /** （可選）平板內容最大寬度；預設 1080 */
  tabletMaxWidth?: number;
};

type Props = {
  options?: LayoutOptions;
};

export default function YunLinResponsiveLayout({ options }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLocale } = useLocalization();

  useEffect(() => {
    setLocale("zh");
  }, []);

  const merged: Required<LayoutOptions> = {
    bgOffsetY: 0,
    footerFixed: true,
    footerHeight: 38, // base (mobile)。平板最少 48，桌機最少 56（樣式內自動提升）
    overflowY: "hidden",
    desktopMaxWidth: 1240,
    tabletMaxWidth: 1080,
    ...options,
  };

  // 切頁時回到頂端（避免沿用上一頁的 scroll 位置）
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    (document.scrollingElement || document.documentElement).scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  // FooterSpacer：若固定 footer，提供對應的內容底部緩衝
  const contentPadBottom = merged.footerFixed ? merged.footerHeight ?? 38 : 0;

  return (
    <Shell $overflowY={merged.overflowY}>
      {/* 背景 */}

      <InstallShortcutBanner />

      <BackgroundFixed aria-hidden>
        <BgCover $offsetY={merged.bgOffsetY} />
        <BgVignette />
      </BackgroundFixed>
      <DustOverlay aria-hidden />

      {/* Header */}
      <HeaderWrap>
        <HeaderPill>
          <BrandLeft onClick={() => navigate("/county-magistrate")}>
            <img
              className="brand"
              style={{ height: 38 }}
              src="/images/yunlin-logo.png"
              alt="Yunlin Logo"
            />
            <span className="pipe">|</span>
            <img
              className="brand"
              src="/images/foxconn-logo.svg"
              alt="Foxconn Logo"
            />
          </BrandLeft>
        </HeaderPill>
      </HeaderWrap>

      {/* Page Content */}
      <ContentWrap $padBottom={contentPadBottom}>
        <ContentInner
          $tabletMax={merged.tabletMaxWidth}
          $desktopMax={merged.desktopMaxWidth}
        >
          <Outlet />
        </ContentInner>
      </ContentWrap>

      {/* Footer */}
      <FooterWrap>
        {merged.footerFixed && (
          <FooterSpacer $height={merged.footerHeight ?? 38} />
        )}
        <FooterOverlay
          $fixed={merged.footerFixed}
          $height={merged.footerHeight}
        >
          <PoweredText>powered by</PoweredText>
          <WhoscallLogo src="/images/whoscall-logo-white.svg" alt="whoscall" />
        </FooterOverlay>
      </FooterWrap>
    </Shell>
  );
}
